import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Divider,
  Grid,
  Badge,
} from '@chakra-ui/react';
import { SaktiBccListItem, SaktiBaListItem } from '../types';
import { SAKTI_UI_SIZE } from '../uiSizes';
import dayjs from 'dayjs';

interface SaktiBccDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: SaktiBccListItem | SaktiBaListItem | null;
}

const SaktiBccDetailModal: React.FC<SaktiBccDetailModalProps> = ({ isOpen, onClose, data }) => {
  if (!data) return null;

  const isBa = 'ba_nbr' in data;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent borderRadius={SAKTI_UI_SIZE.cardRadius}>
        <ModalHeader bg="customGreen.500" color="white" borderRadius={`${SAKTI_UI_SIZE.cardRadius} ${SAKTI_UI_SIZE.cardRadius} 0 0`}>
          {isBa ? `Detail BA - ${data.ba_nbr}` : `Detail BCC - ${data.harvest_nbr}`}
        </ModalHeader>
        <ModalCloseButton color="white" />
        <ModalBody py={6}>
          <VStack align="stretch" spacing={4}>
            {isBa ? (
              <>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <Box>
                    <Text fontSize="xs" color="gray.500">No. Berita Acara</Text>
                    <Text fontWeight="bold">{data.ba_nbr}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">Tanggal</Text>
                    <Text fontWeight="semibold">{dayjs(data.ba_date).format('DD MMMM YYYY')}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">Estate / Divisi</Text>
                    <Text fontWeight="semibold">{data.estate_name} - {data.division_name}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">Tipe Report</Text>
                    <Badge colorScheme={data.report_type === 9 ? 'orange' : 'blue'}>Tipe {data.report_type}</Badge>
                  </Box>
                </Grid>
                <Divider />
                <Box>
                  <Text fontSize="xs" color="gray.500">Catatan</Text>
                  <Text>{data.notes || '-'}</Text>
                </Box>
                <Divider />
                <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                  <Box>
                    <Text fontSize="xs" color="gray.500">Jml BCC</Text>
                    <Text fontSize="xl" fontWeight="bold">{data.bcc_count}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">Total Janjang</Text>
                    <Text fontSize="xl" fontWeight="bold">{data.total_bunch}</Text>
                  </Box>
                  <Box textAlign="right">
                    <Text fontSize="xs" color="gray.500">Total Brondolan</Text>
                    <Text fontSize="xl" fontWeight="bold">{data.total_loose_fruit.toFixed(1)} kg</Text>
                  </Box>
                </Grid>
              </>
            ) : (
              <>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <Box>
                    <Text fontSize="xs" color="gray.500">Pemanen</Text>
                    <Text fontWeight="bold">{data.harvester_name}</Text>
                    <Text fontSize="xs" color="gray.500">{data.harvester_nip}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">Tanggal</Text>
                    <Text fontWeight="semibold">{dayjs(data.harvest_date).format('DD MMMM YYYY')}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">Estate / Divisi</Text>
                    <Text fontWeight="semibold">{data.estate_name} - {data.division_name}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">Blok / TPH</Text>
                    <Text fontWeight="semibold">{data.block_code} / {data.tph_code}</Text>
                  </Box>
                </Grid>
                <Divider />
                <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                  <Box>
                    <Text fontSize="xs" color="gray.500">Jjg Total</Text>
                    <Text fontSize="xl" fontWeight="bold">{data.bunch_qty}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">Jjg Mentah</Text>
                    <Text fontSize="xl" fontWeight="bold">{data.unripe_qty}</Text>
                  </Box>
                  <Box textAlign="right">
                    <Text fontSize="xs" color="gray.500">Brondolan (kg)</Text>
                    <Text fontSize="xl" fontWeight="bold">{data.loose_fruit_qty.toFixed(1)}</Text>
                  </Box>
                </Grid>
                <Divider />
                <Box>
                  <Text fontSize="xs" color="gray.500">Status Pengiriman</Text>
                  {data.transport_id ? (
                    <Badge colorScheme="green">Terkirim (ID: {data.transport_id})</Badge>
                  ) : (
                    <Badge colorScheme="orange">Belum Diangkut (Restan)</Badge>
                  )}
                </Box>
              </>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter bg="gray.50" borderRadius={`0 0 ${SAKTI_UI_SIZE.cardRadius} ${SAKTI_UI_SIZE.cardRadius}`}>
          <Button onClick={onClose}>Tutup</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SaktiBccDetailModal;
